import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { Vote, Plus, Loader2, X, Trash2, ArrowRight, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: "bg-slate/10 text-slate border-slate/30",
  open: "bg-positive-bg text-positive border-positive/30",
  closed: "bg-brick-bg text-brick border-brick/30",
};

export default function Voting() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get("/polls").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <Layout title="Undian / Voting (ALP)" subtitle="Pemilihan & resolusi — undi selamat, satu undi per ahli"
      actions={<button onClick={() => setShowAdd(true)} data-testid="add-poll-btn" className="flex items-center gap-1.5 bg-brass text-ink font-semibold hover:bg-brass-hover px-3 py-2 rounded-sm text-sm transition-colors"><Plus size={16} /> <span className="hidden sm:inline">Cipta Undian</span></button>}>
      {!data ? (
        <div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div>
      ) : data.polls.length === 0 ? (
        <div className="bg-white border border-hairline rounded-sm py-16 text-center text-slate">Tiada undian lagi. / No polls yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {data.polls.map((p) => (
            <button key={p.id} onClick={() => nav(`/voting/${p.id}`)} data-testid={`poll-card-${p.id}`} className="text-left bg-white border border-hairline rounded-sm p-6 hover:border-brass transition-colors group">
              <div className="flex items-start justify-between">
                <span className={`inline-flex items-center border px-2 py-0.5 rounded-sm text-xs capitalize ${STATUS[p.status]}`}>{p.status}</span>
                <ArrowRight size={18} className="text-slate/40 group-hover:text-brass transition-colors" />
              </div>
              <h3 className="font-serif text-xl text-ink mt-3">{p.title}</h3>
              <div className="mt-3 flex items-center gap-4 text-sm text-slate">
                <span className="flex items-center gap-1.5"><Vote size={15} className="text-brass" /> {p.candidates.length} calon</span>
                <span>{p.votes_count} undi</span>
                <span className="text-xs">pilih {p.max_choices}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {showAdd && <AddPollModal onClose={() => setShowAdd(false)} onSaved={(id) => { setShowAdd(false); nav(`/voting/${id}`); }} />}
    </Layout>
  );
}

function AddPollModal({ onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [type, setType] = useState("multi");
  const [maxChoices, setMaxChoices] = useState(1);
  const [candidates, setCandidates] = useState([{ name: "", info: "", photo: "", pencadang: "", penyokong: "" }, { name: "", info: "", photo: "", pencadang: "", penyokong: "" }]);
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setCandidates(candidates.map((x, j) => j === i ? { ...x, ...patch } : x));

  const onPhoto = (i) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await resizeImage(file, 400);
    upd(i, { photo: dataUrl });
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Tajuk diperlukan");
    const cands = candidates.filter((c) => c.name.trim());
    if (type !== "yesno" && cands.length < 2) return toast.error("Sekurang-kurangnya 2 calon");
    setSaving(true);
    try {
      const r = await api.post("/polls", { title, description: desc, poll_type: type, max_choices: parseInt(maxChoices) || 1, candidates: cands });
      toast.success("Undian dicipta");
      onSaved(r.data.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Cipta Undian / Create Poll</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 space-y-4">
          <div><label className="block text-xs font-medium text-slate mb-1">Tajuk / Title *</label><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="poll-title" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" placeholder="Pemilihan ALP 2026" /></div>
          <div><label className="block text-xs font-medium text-slate mb-1">Keterangan / Description</label><textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs font-medium text-slate mb-1">Jenis / Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)} data-testid="poll-type" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink">
                <option value="multi">Pilih beberapa / Multiple</option><option value="single">Pilih satu / Single</option><option value="yesno">Ya / Tidak</option>
              </select></div>
            {type === "multi" && <div><label className="block text-xs font-medium text-slate mb-1">Maks pilihan / Max choices</label><input type="number" min={1} value={maxChoices} onChange={(e) => setMaxChoices(e.target.value)} data-testid="poll-maxchoices" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" /></div>}
          </div>
          {type !== "yesno" && (
            <div>
              <label className="block text-xs font-medium text-slate mb-2">Calon / Candidates (gambar + nama + jawatan)</label>
              <div className="space-y-3">
                {candidates.map((c, i) => (
                  <div key={i} className="flex gap-3 items-start bg-paper border border-hairline rounded-sm p-3">
                    <label className="flex-shrink-0 cursor-pointer" data-testid={`candidate-photo-${i}`}>
                      <input type="file" accept="image/*" className="hidden" onChange={onPhoto(i)} />
                      {c.photo ? (
                        <img src={c.photo} alt="" className="w-14 h-14 rounded-full object-cover border border-hairline" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-white border-2 border-dashed border-slate/30 flex items-center justify-center text-slate/50"><ImagePlus size={18} /></div>
                      )}
                    </label>
                    <div className="flex-1 space-y-2">
                      <input value={c.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`candidate-${i}`} placeholder={`Nama Calon ${i + 1}`} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" />
                      <input value={c.info} onChange={(e) => upd(i, { info: e.target.value })} placeholder="Jawatan / maklumat (pilihan)" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-1.5 text-xs outline-none focus:border-brass text-slate" />
                      <div className="grid grid-cols-2 gap-2">
                        <input value={c.pencadang} onChange={(e) => upd(i, { pencadang: e.target.value })} data-testid={`candidate-pencadang-${i}`} placeholder="Pencadang / Proposer" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-1.5 text-xs outline-none focus:border-brass text-slate" />
                        <input value={c.penyokong} onChange={(e) => upd(i, { penyokong: e.target.value })} data-testid={`candidate-penyokong-${i}`} placeholder="Penyokong / Seconder" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-1.5 text-xs outline-none focus:border-brass text-slate" />
                      </div>
                    </div>
                    {candidates.length > 2 && <button onClick={() => setCandidates(candidates.filter((_, j) => j !== i))} className="text-brick hover:bg-brick-bg p-2 rounded-sm flex-shrink-0"><Trash2 size={15} /></button>}
                  </div>
                ))}
              </div>
              <button onClick={() => setCandidates([...candidates, { name: "", info: "", photo: "", pencadang: "", penyokong: "" }])} data-testid="add-candidate" className="mt-2 text-sm text-brass hover:text-brass-hover flex items-center gap-1"><Plus size={14} /> Tambah calon</button>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="poll-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Simpan</button>
        </div>
      </div>
    </div>
  );
}

function resizeImage(file, maxSize) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxSize) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else if (height > maxSize) { width = Math.round((width * maxSize) / height); height = maxSize; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}
